import React from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { AnimatedSvg, type LineAnimation } from './AnimatedSvg';

// The duration that start and end times are based on, from figma export
const ANIMATION_DURATION = 4.78334;
const DEFAULT_DURATION = 3;

// Path lengths calculated using SVG path.getTotalLength() and hardcoded so
// that styles don't rely on rendering svg first
const LINE_ANIMATIONS = (duration: number): LineAnimation[] => {
  const speed = duration / ANIMATION_DURATION;

  return [
    {
      id: 'circle',
      length: 59,
      startsDrawn: true,
      actions: [{ type: 'undraw', startTime: 3.86667 * speed, endTime: 4.28334 * speed }],
    },
    {
      id: 'circle-reversed',
      length: 59,
      startsDrawn: false,
      actions: [{ type: 'draw', startTime: 4.3 * speed, endTime: 4.66667 * speed }],
    },
    {
      id: 'line1',
      length: 15.4,
      actions: [
        { type: 'draw', startTime: 0 * speed, endTime: 0.23334 * speed },
        { type: 'undraw', startTime: 2.43334 * speed, endTime: 2.68334 * speed },
      ],
    },
    {
      id: 'line2',
      length: 19,
      actions: [
        { type: 'draw', startTime: 0.35 * speed, endTime: 0.65004 * speed },
        { type: 'undraw', startTime: 2.78334 * speed, endTime: 3.06667 * speed },
      ],
    },
    {
      id: 'line3',
      length: 19,
      actions: [
        { type: 'draw', startTime: 0.71667 * speed, endTime: 1.01667 * speed },
        { type: 'undraw', startTime: 3.16667 * speed, endTime: 3.43334 * speed },
      ],
    },
    {
      id: 'line4',
      length: 16.9,
      actions: [
        { type: 'draw', startTime: 1.1 * speed, endTime: 1.31667 * speed },
        { type: 'undraw', startTime: 3.55 * speed, endTime: 3.8 * speed },
      ],
    },
  ] as const satisfies LineAnimation[];
};

type LineAnimationId = 'circle' | 'circle-reversed' | 'line1' | 'line2' | 'line3' | 'line4';

export const AnimatedLogo: React.FC<{
  size?: number;
  className?: string;
  autoPlay?: boolean;
  duration?: number;
}> = ({ size = 40, className, autoPlay = true, duration = DEFAULT_DURATION }) => {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  return (
    <AnimatedSvg
      className={className}
      autoPlay={autoPlay && !prefersReducedMotion}
      animations={LINE_ANIMATIONS(duration)}
      animationDuration={duration}
      animationClass="animate-logo"
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#animatedLogoClipPath)">
          <path
            className={'circle' satisfies LineAnimationId}
            d="M10.01 0.620117C15.1959 0.620117 19.4 4.82416 19.4 10.0101C19.4 15.1961 15.1959 19.4001 10.01 19.4001C4.82404 19.4001 0.619995 15.1961 0.619995 10.0101C0.619995 4.82416 4.82404 0.620117 10.01 0.620117Z"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeMiterlimit="10"
            fill="none"
          />

          <path
            className={'circle-reversed' satisfies LineAnimationId}
            d="M10.01 0.620117C4.82404 0.620117 0.619995 4.82416 0.619995 10.0101C0.619995 15.1961 4.82404 19.4001 10.01 19.4001C15.1959 19.4001 19.4 15.1961 19.4 10.0101C19.4 4.82416 15.1959 0.620117 10.01 0.620117Z"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeMiterlimit="10"
            fill="none"
          />

          <path
            className={'line1' satisfies LineAnimationId}
            d="M0.640015 10.4999C1.56001 7.30992 3.04001 6.26992 6.49001 4.90992C10.4 3.37992 11.82 0.669922 11.82 0.669922"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeMiterlimit="10"
            fill="none"
          />

          <path
            className={'line2' satisfies LineAnimationId}
            d="M2.48999 15.6299C2.99999 10.9699 4.72999 9.67993 8.93999 7.62993C13.15 5.57993 14.12 1.67993 14.12 1.67993"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeMiterlimit="10"
            fill="none"
          />

          <path
            className={'line3' satisfies LineAnimationId}
            d="M8.49999 19.0499C7.77999 14.4199 9.22999 13.3199 11.77 10.0099C13.81 7.3599 14.26 4.0299 14.28 1.6499"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeMiterlimit="10"
            fill="none"
          />

          <path
            className={'line4' satisfies LineAnimationId}
            d="M8.29004 19.2399C9.01004 16.9599 10.78 15.6299 13.81 14.0999C17.25 12.3699 18.47 9.26994 18.88 6.93994"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeMiterlimit="10"
            fill="none"
          />
        </g>
        <defs>
          <clipPath id="animatedLogoClipPath">
            <rect width="40" height="40" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </AnimatedSvg>
  );
};
